import { FormFieldErrorContainer } from "../../CoreFields/FieldError/FormFieldErrorContainer";
import { FieldLabel } from "../../CoreFields/FieldLabel/FieldLabel";
import {
  AsyncMultiSelectTreeFieldValue,
  AsyncMultiSelectTreeProps,
  FieldLabeledProps,
} from "../../types/fieldTypes";
import { AsyncODataFieldProps } from "../types";
import { Predicate } from "@syncfusion/ej2-data";
import { useAsyncMultiSelectTreeField } from "./useAsyncMultiSelectTreeField";
import {
  DdtChangeEventArgs,
  DropDownTreeComponent,
  FilteringEventArgs,
} from "@syncfusion/ej2-react-dropdowns";

export interface AsyncMultiSelectTreeFieldProps
  extends AsyncMultiSelectTreeProps,
    Omit<Partial<AsyncODataFieldProps>, "isValueMultiple"> {
  value: AsyncMultiSelectTreeFieldValue;
  labelProps?: FieldLabeledProps;
  conditions?: Predicate[];
  optionFields?: string[];
  groupByActiveStatus?: boolean;
  noRecordsTemplate?: (ref: any) => React.ReactNode;
  readonly isValueMultiple?: never;
}

const AsyncMultiSelectTreeField: React.FC<AsyncMultiSelectTreeFieldProps> = (
  props
) => {
  // Ensure isValueMultiple is always true for AsyncMultiselectField
  const propsWithIsValueMultiple = {
    ...props,
    isValueMultiple: true,
  } as AsyncMultiSelectTreeFieldProps & { isValueMultiple: true };

  const {
    getQuery,
    dataSource,
    setNoRecordsTemplate,
    localOptions,
    setLocalOptions,
    multiSelectRef,
    sanitizeValue,
    multiselectTreeSettings,
  } = useAsyncMultiSelectTreeField(propsWithIsValueMultiple);

  // // only render when data source is ready
  if (!dataSource) {
    return null;
  }

  setNoRecordsTemplate();

  return (
    <section
      className={`${
        (props.isRequired && !props.value) ||
        (props.isRequired && !!props.value && props.value.length === 0) ||
        (props.isRequired &&
          !!props.value &&
          props.value.filter((x) => x !== null && x !== undefined).length === 0)
          ? "has-required-background"
          : "ucl-async-multiselect-tree-field"
      }`}
    >
      {props.label && (
        <FieldLabel
          labelName={props.label}
          tooltipDescription={props.description}
          isRequired={props.isRequired}
          {...props.labelProps}
        />
      )}
      <div className="ucl-async-multiselect-tree-toolbar">
        <div className="ucl-async-multiselect-tree-input-container">
          <DropDownTreeComponent
            className="ucl-async-multiselect-tree-input"
            ref={multiSelectRef}
            value={sanitizeValue(props.value)}
            treeSettings={{
              autoCheck: false,
              checkDisabledChildren: true,
              expandOn: "Click",
              loadOnDemand: true,
            }}
            allowMultiSelection={true}
            allowFiltering={true}
            changeOnBlur={true}
            disabled={props.disabled}
            readOnly={props.disabled}
            showCheckBox={true}
            dataBound={(event) => {
              setLocalOptions(event.data);
            }}
            change={async (e: DdtChangeEventArgs) => {
              // Syncfusion emits a change event on load of the initial data.
              // We only want to update the form state if the change was user-intiated.
              if (e.isInteracted) {
                const defaultOpt: any = { label: "", value: "" };
                const submitValue =
                  e.value.length === 0
                    ? [defaultOpt]
                    : e.value.map(
                        (value) =>
                          // Syncfusion returns a list of the currently selected option values,
                          // but we want more metadata.
                          localOptions?.find((opt) => opt.value === value) ||
                          defaultOpt
                      );
                props.onSubmit && props.onSubmit(submitValue);
              }
            }}
            filtering={(e: FilteringEventArgs) => {
              if (e) {
                e.preventDefaultAction = true;
                const query = getQuery(e.text);

                if (dataSource && e.updateData) {
                  e.updateData(dataSource, query);
                }
              }
            }}
            {...multiselectTreeSettings}
          />
        </div>
      </div>
      {props.errorMessages && (
        <FormFieldErrorContainer errorMessages={props.errorMessages} />
      )}
    </section>
  );
};

export default AsyncMultiSelectTreeField;
